import React, { useEffect, useState, useCallback } from "react";
import ecoisAPI from "../../../api/ecois";
import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Tab,
  Tabs,
  ThemeProvider,
  Toolbar,
  Typography,
  List,
  ListItem as ProtoListItem,
  ListItemText,
  ListItemAvatar,
  Table,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  useMediaQuery,
  MenuItem,
  Menu,
  Tooltip,
  Alert,
} from "@mui/material";
import {
  InfoOutlined,
  Add,
  Refresh,
  SelectAll,
  Sort,
  ViewList,
  ViewModule,
  Warning,
  ExpandMore,
  ChevronRight,
  MoreVert,
  Settings,
  BarChart,
} from "@mui/icons-material";
import useSort from "../../../utils/useSort";
import base64ToImg from "../../../utils/base64ToImg";
import biosenseAPI from "../../../api/biosense";
import noImage from "../../../assets/noImage.json";
import DeviceDescription from "../../components/deviceDescription";
import SwalMedia from "../../components/swalMedia";
import { useTranslation } from "react-i18next";
import cardOrListTheme from "../../../assets/cardOrListTheme";
import getObjectSeriesAbbrBySn from "../../../utils/getObjectSeriesAbbrBySn";
import toFlattenedObj from "../../../utils/toFlattenedObj";
import AddDeviceComponent from "../mainComponents/project/addDevice";
import ProjectDetails from "../mainComponents/project/projectDetails";

const phoneTheme = cardOrListTheme;
const theme = cardOrListTheme;

export default function ProjectScreen({
  state,
  setState,
  projectState,
  historyState,
  setHistoryState,
  topBarState,
  setDeviceDetailsState,
}) {
  const project_id = projectState.selectedProject.id ?? projectState.selectedProject;
  const [devices, setDevices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const alertDevices = topBarState.alerts.map((alert) => alert?.device_id);
  const alertTexts = topBarState.alerts.map((alert) => alert?.alert_text);
  const [deviceDesc, setDeviceDesc] = useState(null);
  const [sensorLatest, setSensorLatest] = useState(null);
  const [expandIdx, setExpandIdx] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [sortedDevices, handleSort, SortFieldIcon] = useSort(devices, "alias");
  const [addDeviceOpen, setAddDeviceOpen] = useState(false);
  const { t } = useTranslation();

  // 使用 useCallback 缓存函数，避免重复创建
  const handleHistoryButtonOnClick = useCallback(
    (device, deviceTypes) => {
      setState({
        pageTabStack: [...state.pageTabStack, state.pageTab],
        pageTab: "history",
        pageIdxStack: [...state.pageIdxStack, state.pageIdx],
        pageIdx: 1,
      });
      setHistoryState({
        project: device.project_alias ?? device.alias, // ecos do not have project, NEED to combine api later
        history: {
          ...historyState.history,
          seriesAbbr: device.seriesAbbr,
          sn: device.sn,
          availableTypes: deviceTypes,
          latest: sensorLatest?.datetime,
        },
      });
    },
    [state, setState, setHistoryState, historyState, sensorLatest],
  );

  const handleDeviceDetailsOnClick = useCallback(
    (device) => {
      setState({
        pageTabStack: [...state.pageTabStack, state.pageTab],
        pageTab: "deviceDetails",
        pageIdxStack: [...state.pageIdxStack, state.pageIdx],
        pageIdx: 1,
      });
      setDeviceDetailsState({
        project: device.project_alias ?? device.alias, // ecos do not have project, NEED to combine api later
        device: device,
      });
    },
    [state, setState, setDeviceDetailsState],
  );

  const handleTabChange = useCallback((e, newValue) => {
    setTabValue(newValue);
  }, []);

  const handleRefresh = useCallback(() => {
    setLastUpdated(new Date());
  }, []);

  const handleAddClick = useCallback(() => {
    setAddDeviceOpen(true);
  }, []);

  const handleSelectClick = useCallback(() => {
    console.log("Select button clicked");
  }, []);

  // 使用 useEffect 依赖项数组，避免不必要的重新渲染
  useEffect(() => {
    const fetchDevices = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await biosenseAPI.getDeviceListByProject({ project_id });
        setDevices(toFlattenedObj(res.devices));
      } catch (err) {
        setError("Failed to load devices");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDevices();
  }, [lastUpdated, project_id]);

  useEffect(() => {
    if (expandIdx === null) return;

    setDeviceDesc(null);
    setSensorLatest(null);

    const objectName = getObjectSeriesAbbrBySn(devices, expandIdx);
    const fetchDeviceData = async () => {
      try {
        if (objectName === "esp32s3") {
          const [descRes, valueRes] = await Promise.all([
            biosenseAPI.getDeviceDescription({ sn: expandIdx }),
            biosenseAPI.getDeviceValueListLatest({ sn: expandIdx }),
          ]);
          setDeviceDesc(descRes);
          setSensorLatest(valueRes);
        } else {
          biosenseAPI.getDeviceMode({ sn: expandIdx }).then((modeRes) => {
            ecoisAPI.getDeviceDescription({ sn: expandIdx }).then((res) => {
              const mode = modeRes.mode;
              if (mode === null) {
                setDeviceDesc(res);
              } else {
                const parameters = res.parameters[0];
                if (mode === "soilwater" && parameters.waterLevel) {
                  delete parameters.waterLevel;
                } else if (mode === "waterlevel" && parameters.soilwater) {
                  delete parameters.soilwater;
                }
                setDeviceDesc(res);
              }
            });
          });
          ecoisAPI.getSensorValueListLatest({ sn: expandIdx }).then((res) => setSensorLatest(res));
        }
      } catch (err) {
        setError("Failed to load device details");
        console.error(err);
      }
    };

    fetchDeviceData();
  }, [expandIdx, devices]);

  // 响应式断点
  const isXSmall = useMediaQuery(theme.breakpoints.down("xs"));
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.down("md"));

  // 溢出菜单状态
  const [anchorEl, setAnchorEl] = useState(null);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const openOverflow = Boolean(anchorEl);
  const openSort = Boolean(sortAnchorEl);

  // 排序选项
  const sortOptions = [
    { field: "alias", text: t("name") },
    { field: "sn", text: t("serialnumber") },
  ];

  // 处理排序菜单打开
  const handleSortClick = useCallback((event) => {
    setSortAnchorEl(event.currentTarget);
  }, []);

  // 处理排序选择
  const handleSortSelect = useCallback(
    (field) => {
      handleSort(field);
      setSortAnchorEl(null);
    },
    [handleSort],
  );

  // 处理溢出菜单打开
  const handleOverflowClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  // 处理溢出菜单关闭
  const handleOverflowClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // 渲染排序菜单
  const renderSortMenu = () => (
    <Menu
      anchorEl={sortAnchorEl}
      open={openSort}
      onClose={() => setSortAnchorEl(null)}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
    >
      {sortOptions.map((item, idx) => (
        <MenuItem key={idx} onClick={() => handleSortSelect(item.field)} sx={{ minWidth: 180 }}>
          <IconButton
            style={{
              flexDirection: "row",
              alignItems: "center",
              borderRadius: "4px",
              padding: "8px",
              gap: 1,
              mr: 2,
            }}
            size='small'
          >
            <SortFieldIcon field={item.field} />
          </IconButton>
          <Typography variant='body1'>{item.text}</Typography>
        </MenuItem>
      ))}
    </Menu>
  );

  // 渲染溢出菜单
  const renderOverflowMenu = () => (
    <Menu
      anchorEl={anchorEl}
      open={openOverflow}
      onClose={handleOverflowClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <MenuItem onClick={handleRefresh}>
        <Refresh fontSize='small' sx={{ mr: 2 }} />
        <Typography variant='body1'>{t("refresh")}</Typography>
      </MenuItem>
      <MenuItem onClick={handleAddClick}>
        <Add fontSize='small' sx={{ mr: 2 }} />
        <Typography variant='body1'>{t("add")}</Typography>
      </MenuItem>
      <MenuItem onClick={handleSelectClick}>
        <SelectAll fontSize='small' sx={{ mr: 2 }} />
        <Typography variant='body1'>{t("select")}</Typography>
      </MenuItem>
    </Menu>
  );

  // 工具栏组件
  const ProjectToolBar = () => {
    return (
      <AppBar position='static' sx={{ backgroundColor: "white", boxShadow: 4 }}>
        <Toolbar
          sx={{
            columnGap: 1,
            flexWrap: "wrap",
            paddingLeft: isXSmall ? 1 : 2,
            paddingRight: isXSmall ? 1 : 2,
          }}
        >
          {/* 视图切换标签 */}
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label='data display tabs'
            variant={isXSmall ? "fullWidth" : "standard"}
            sx={{
              "& .MuiTabs-flexContainer": {
                flexWrap: "nowrap",
              },
              mr: isMedium ? 1 : 2,
            }}
          >
            <Tab
              icon={<ViewModule />}
              label={isXSmall ? null : t("list.card")}
              aria-label='Cards'
              sx={{
                flexDirection: isXSmall ? "column" : "row",
                alignItems: "center",
                gap: 1,
                minWidth: isXSmall ? "auto" : undefined,
              }}
            />
            <Tab
              icon={<ViewList />}
              label={isXSmall ? null : t("list.list")}
              aria-label='List'
              sx={{
                flexDirection: isXSmall ? "column" : "row",
                alignItems: "center",
                gap: 1,
                minWidth: isXSmall ? "auto" : undefined,
              }}
            />
          </Tabs>

          {/* 排序部分 */}
          {!isSmall && (
            <Box display='flex' alignItems='center' mr={isMedium ? 1 : 2}>
              <Typography variant='body1' color='grey' mr={1}>
                {t("list.sort")}:
              </Typography>

              {isMedium ? (
                <IconButton
                  onClick={handleSortClick}
                  size='small'
                  aria-controls='sort-menu'
                  aria-haspopup='true'
                >
                  <Sort />
                  <Typography variant='body1' ml={1}>
                    {sortOptions.find((opt) => opt.field === handleSort.currentField)?.text ||
                      t("name")}
                  </Typography>
                  <ChevronRight fontSize='small' />
                </IconButton>
              ) : (
                <>
                  {sortOptions.map((item, idx) => (
                    <IconButton
                      key={idx}
                      onClick={() => handleSort(item.field)}
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        borderRadius: "4px",
                        padding: "8px",
                        gap: 1,
                      }}
                      size='small'
                    >
                      <SortFieldIcon field={item.field} />
                      <Typography variant='body1'>{item.text}</Typography>
                    </IconButton>
                  ))}
                </>
              )}
            </Box>
          )}

          {/* 右侧操作按钮 */}
          <Box sx={{ flexGrow: 1 }} />

          {!isMedium && (
            <>
              <Typography variant='body' color='grey' mr={2}>
                {t("list.lastupdatedat")}: {lastUpdated.toLocaleString("zh-CN")}
              </Typography>

              <Tooltip title={t("refresh")}>
                <IconButton aria-label='refresh' onClick={handleRefresh} size='small'>
                  <Refresh />
                </IconButton>
              </Tooltip>

              <Tooltip title={t("add")}>
                <IconButton aria-label='add' onClick={handleAddClick} size='small'>
                  <Add />
                </IconButton>
              </Tooltip>

              <Tooltip title={t("select")}>
                <IconButton aria-label='select' edge='end' onClick={handleSelectClick} size='small'>
                  <SelectAll />
                </IconButton>
              </Tooltip>
            </>
          )}

          {/* 溢出菜单 - 小屏幕时显示 */}
          {isMedium && (
            <>
              <Typography
                variant='body'
                color='grey'
                mr={2}
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {t("list.lastupdatedat")}: {lastUpdated.toLocaleString("zh-CN")}
              </Typography>

              <IconButton
                aria-label='more'
                aria-controls='overflow-menu'
                aria-haspopup='true'
                onClick={handleOverflowClick}
                size='small'
              >
                <MoreVert />
              </IconButton>
            </>
          )}
        </Toolbar>

        {/* 第二行 - 仅在超小屏幕显示 */}
        {isXSmall && (
          <Toolbar
            variant='dense'
            sx={{
              paddingTop: 0,
              paddingLeft: isXSmall ? 1 : 2,
              paddingRight: isXSmall ? 1 : 2,
            }}
          >
            <Typography variant='body2' color='grey' sx={{ flexGrow: 1 }}>
              {t("list.lastupdatedat")}: {lastUpdated.toLocaleString("zh-CN")}
            </Typography>

            {!isSmall && (
              <IconButton
                aria-label='sort'
                aria-controls='sort-menu'
                aria-haspopup='true'
                onClick={handleSortClick}
                size='small'
              >
                <Sort />
              </IconButton>
            )}
          </Toolbar>
        )}

        {renderSortMenu()}
        {renderOverflowMenu()}
      </AppBar>
    );
  };

  // 加载状态显示
  if (loading) {
    return (
      <Box display={"flex"} flexDirection={"column"} rowGap={3} height={"100%"}>
        <ProjectDetails projectId={project_id} />
        <ProjectToolBar />
        <Box flexGrow={1} display='flex' justifyContent='center' alignItems='center'>
          <CircularProgress sx={{ mt: 1 }} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display={"flex"} flexDirection={"column"} rowGap={3} height={"100%"}>
        <ProjectDetails projectId={project_id} />
        <ProjectToolBar />
        <Box flexGrow={1} display='flex' justifyContent='center' alignItems='center'>
          <Alert severity='error'>{error}</Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box display={"flex"} flexDirection={"column"} rowGap={3}>
      <ProjectDetails projectId={project_id} />
      <ProjectToolBar />
      {tabValue ? (
        <List
          sx={{
            backgroundColor: "white",
            p: 1.5,
            borderRadius: "4px",
            boxShadow: 4,
          }}
        >
          {sortedDevices.map((device, idx) => (
            <ListItem
              key={`device-list-${idx}`}
              device={device}
              expandIdx={expandIdx}
              setExpandIdx={setExpandIdx}
              handleHistoryButtonOnClick={handleHistoryButtonOnClick}
              handleDeviceDetailsButtonOnClick={handleDeviceDetailsOnClick}
              deviceDesc={deviceDesc}
              sensorLatest={sensorLatest}
              img={device.img ?? noImage.img}
              t={t}
              alertDevices={alertDevices}
              alertTexts={alertTexts}
            />
          ))}
        </List>
      ) : (
        <ThemeProvider theme={theme}>
          <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12, lg: 12, xl: 12 }}>
            {sortedDevices.map((device, idx) => (
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={4}
                xl={3}
                key={device.sn}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-start",
                }}
              >
                <Card
                  key={`site-card-${idx}`}
                  device={device}
                  expandIdx={expandIdx}
                  setExpandIdx={setExpandIdx}
                  handleHistoryButtonOnClick={handleHistoryButtonOnClick}
                  handleDeviceDetailsButtonOnClick={handleDeviceDetailsOnClick}
                  deviceDesc={deviceDesc}
                  sensorLatest={sensorLatest}
                  img={device.img ?? noImage.img}
                  t={t}
                  alertDevices={alertDevices}
                  alertTexts={alertTexts}
                />
              </Grid>
            ))}
          </Grid>
        </ThemeProvider>
      )}

      {addDeviceOpen && (
        <AddDeviceComponent
          projectId={project_id}
          open={addDeviceOpen}
          onClose={() => setAddDeviceOpen(false)}
          onDeviceAdded={() => setLastUpdated(new Date())}
        />
      )}
    </Box>
  );
}

const ExpandButton = ({ isCurrentExpanded, setExpandIdx, sn, t, size = "medium" }) => {
  return (
    <Button
      variant='contained'
      size={size}
      sx={{ borderRadius: "4px", textTransform: "none" }}
      color={isCurrentExpanded ? "warning" : "success"}
      onClick={() => (isCurrentExpanded ? setExpandIdx(null) : setExpandIdx(sn))}
      startIcon={isCurrentExpanded ? <ExpandMore /> : null}
    >
      {isCurrentExpanded ? t("list.close") : t("recentdata")}
    </Button>
  );
};

const DeviceButton = ({ handleButtonOnClick, t, size = "medium" }) => {
  return (
    <Button
      variant='contained'
      size={size}
      sx={{ borderRadius: "4px", textTransform: "none" }}
      color={"warning"}
      onClick={handleButtonOnClick}
    >
      {t("list.details")}
    </Button>
  );
};

const Card = ({
  device,
  expandIdx,
  setExpandIdx,
  handleHistoryButtonOnClick,
  handleDeviceDetailsButtonOnClick,
  deviceDesc,
  sensorLatest,
  img,
  t,
  alertDevices,
  alertTexts,
}) => {
  const isPhone = useMediaQuery(phoneTheme.breakpoints.down("sm"));
  const deviceId = device.id;
  const isAlertDevice = alertDevices.includes(deviceId);
  const alertIndex = isAlertDevice ? alertDevices.indexOf(deviceId) : -1;
  const alertText = isAlertDevice ? alertTexts[alertIndex] : "";
  const isCurrentExpanded = expandIdx === device.sn;
  const deviceTypes = Object.keys(deviceDesc?.parameters?.[0] || {});

  // 渲染设备基本信息表格
  const renderDeviceInfoTable = () => (
    <Table sx={{ borderCollapse: "collapse" }}>
      <TableBody>
        <TableRow sx={{ borderBottom: "none" }}>
          <TableCell sx={{ padding: "6px 12px", borderBottom: "none", width: "70px" }}>
            {t("serialnumber")}:
          </TableCell>
          <TableCell sx={{ padding: "6px 12px", borderBottom: "none", textAlign: "left" }}>
            {device.sn}
          </TableCell>
        </TableRow>
        <TableRow sx={{ borderBottom: "none" }}>
          <TableCell sx={{ padding: "6px 12px", borderBottom: "none", width: "70px" }}>
            {t("name")}:
          </TableCell>
          <TableCell sx={{ padding: "6px 12px", borderBottom: "none", textAlign: "left" }}>
            {device.alias ?? "(沒有名字)"}
          </TableCell>
        </TableRow>
        <TableRow sx={{ borderBottom: "none" }}>
          <TableCell sx={{ padding: "6px 12px", borderBottom: "none", width: "70px" }}>
            {t("series")}:
          </TableCell>
          <TableCell sx={{ padding: "6px 12px", borderBottom: "none", textAlign: "left" }}>
            {device.series} - {device.type}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );

  // 渲染移动设备上的图标按钮
  const renderPhoneButtons = () => (
    <Grid container spacing={1} justifyContent='center' mt={1}>
      <Grid item>
        <Tooltip title={t("list.details")}>
          <IconButton
            size='small'
            color='warning'
            onClick={() => handleDeviceDetailsButtonOnClick(device)}
          >
            <Settings fontSize='small' />
          </IconButton>
        </Tooltip>
        <Typography variant='caption' sx={{ fontSize: 9, textAlign: "center" }}>
          {t("list.details")}
        </Typography>
      </Grid>
      <Grid item>
        <Tooltip title={isCurrentExpanded ? t("list.close") : t("recentdata")}>
          <IconButton
            size='small'
            color={isCurrentExpanded ? "warning" : "success"}
            onClick={() => (isCurrentExpanded ? setExpandIdx(null) : setExpandIdx(device.sn))}
          >
            <BarChart fontSize='small' />
          </IconButton>
        </Tooltip>
        <Typography variant='caption' sx={{ fontSize: 9, textAlign: "center" }}>
          {isCurrentExpanded ? t("list.close") : t("recentdata")}
        </Typography>
      </Grid>
    </Grid>
  );

  // 渲染桌面设备上的普通按钮
  const renderDesktopButtons = () => (
    <Box display='flex' flexDirection='column' justifyContent='center' width='8em' gap={1.5}>
      <DeviceButton
        device={device}
        handleButtonOnClick={() => handleDeviceDetailsButtonOnClick(device)}
        t={t}
      />
      <ExpandButton
        isCurrentExpanded={isCurrentExpanded}
        setExpandIdx={setExpandIdx}
        sn={device.sn}
        t={t}
      />
    </Box>
  );

  return (
    <Box
      display='flex'
      flexDirection='column'
      width='100%'
      sx={{
        backgroundColor: "white",
        p: 1.5,
        borderRadius: "4px",
        boxShadow: 4,
        borderBottom: isAlertDevice ? "6px solid #FF9966" : "none",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: 6,
          transform: "translateY(-2px)",
        },
      }}
    >
      <Box display='flex' flexDirection='row' justifyContent='space-between'>
        <SwalMedia
          component='img'
          isWithBlur={false}
          title={device.alias ?? "(沒有名字)"}
          height='150'
          width='150'
          style={{
            objectFit: "cover",
            objectPosition: "0 -50px",
            borderRadius: "4px 4px 0 0",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
          src={base64ToImg({ base64String: img })}
          alt='Card Background'
        />

        <Box display='flex' flexDirection='column' width='calc(100% - 100px)' /* 调整宽度 */>
          <Box display='flex' flexDirection='row' justifyContent='space-between'>
            <Box>
              {renderDeviceInfoTable()}

              {isAlertDevice ? (
                <Box display='flex' alignItems='center' mt={1}>
                  <Chip
                    sx={{
                      p: 0.25,
                      backgroundColor: "#ff996688",
                      color: "black",
                      zIndex: 1,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontSize: 10,
                    }}
                    label={alertText}
                    icon={<Warning color='white' />}
                  />
                  <Tooltip title={alertText} sx={{ ml: 1 }}>
                    <IconButton size='small' color='inherit'>
                      <InfoOutlined />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : null}
            </Box>

            {!isPhone && renderDesktopButtons()}
          </Box>
        </Box>
      </Box>

      {/* 按钮在移动设备上显示在下方 */}
      {isPhone && renderPhoneButtons()}

      <DeviceDescription
        device={device}
        expandIdx={expandIdx}
        deviceDesc={deviceDesc}
        sensorLatest={sensorLatest}
        historyButtonOnClick={() => handleHistoryButtonOnClick(device, deviceTypes)}
      />
    </Box>
  );
};

const ListItem = ({
  device,
  expandIdx,
  setExpandIdx,
  handleHistoryButtonOnClick,
  handleDeviceDetailsButtonOnClick,
  deviceDesc,
  sensorLatest,
  img,
  t,
  alertDevices,
  alertTexts,
}) => {
  const isPhone = useMediaQuery(phoneTheme.breakpoints.down("sm"));
  const deviceId = device.id;
  const isAlertDevice = alertDevices.includes(deviceId);
  const alertIndex = isAlertDevice ? alertDevices.indexOf(deviceId) : -1;
  const alertText = isAlertDevice ? alertTexts[alertIndex] : "";
  const listTitle = `${device.series} - ${device.type} - ${device.alias ?? "(沒有名字)"}`;
  const isCurrentExpanded = expandIdx === device.sn;
  const deviceTypes = Object.keys(deviceDesc?.parameters?.[0] || {});

  // 移动设备上的图标按钮，修改为横向排列
  const renderPhoneButtons = () => (
    <Box display='flex' flexDirection='row' gap={1} alignItems='center'>
      <Box display='flex' flexDirection='column' alignItems='center'>
        <Tooltip title={t("list.details")}>
          <IconButton
            size='small'
            color='warning'
            onClick={() => handleDeviceDetailsButtonOnClick(device)}
          >
            <Settings fontSize='small' />
          </IconButton>
        </Tooltip>
        <Typography variant='caption' sx={{ fontSize: 10 }}>
          {t("list.details")}
        </Typography>
      </Box>
      <Box display='flex' flexDirection='column' alignItems='center'>
        <Tooltip title={isCurrentExpanded ? t("list.close") : t("recentdata")}>
          <IconButton
            size='small'
            color={isCurrentExpanded ? "warning" : "success"}
            onClick={() => (isCurrentExpanded ? setExpandIdx(null) : setExpandIdx(device.sn))}
          >
            <BarChart fontSize='small' />
          </IconButton>
        </Tooltip>
        <Typography variant='caption' sx={{ fontSize: 10 }}>
          {isCurrentExpanded ? t("list.close") : t("recentdata")}
        </Typography>
      </Box>
    </Box>
  );

  // 桌面设备上的普通按钮
  const renderDesktopButtons = () => (
    <Box display='flex' flexDirection='row' gap={1} alignItems='center'>
      <DeviceButton
        device={device}
        handleButtonOnClick={() => handleDeviceDetailsButtonOnClick(device)}
        t={t}
        size='small'
      />
      <ExpandButton
        isCurrentExpanded={isCurrentExpanded}
        setExpandIdx={setExpandIdx}
        sn={device.sn}
        t={t}
        size='small'
      />
    </Box>
  );

  return (
    <li key={`device-list-item-${device.sn}`}>
      <ProtoListItem
        sx={{
          borderLeft: isAlertDevice ? "6px solid #FF9966" : "none",
          padding: 2,
          "&:last-child": {
            borderBottom: 0,
          },
        }}
      >
        <ListItemAvatar>
          <SwalMedia
            component='Avatar'
            title={device.alias ?? "(沒有名字)"}
            height='60'
            width='60'
            style={{
              objectFit: "cover",
            }}
            src={base64ToImg({ base64String: img })}
            alt='List Avatar'
          />
        </ListItemAvatar>
        <Box display='flex' flexDirection='column' flexGrow={1}>
          <ListItemText
            sx={{
              flexGrow: 1,
              "& .MuiListItemText-secondary": {
                whiteSpace: "pre-wrap",
                fontSize: 13,
              },
            }}
            primary={
              <Box display='flex' alignItems='center'>
                <Typography variant='body1' fontWeight={500}>
                  {listTitle}
                </Typography>
                {!isPhone && isAlertDevice ? (
                  <Chip
                    sx={{
                      ml: 1,
                      p: 0.25,
                      backgroundColor: "#ff996688",
                      color: "black",
                      zIndex: 1,
                      fontSize: 11,
                    }}
                    label={alertText}
                    icon={<Warning color='black' fontSize='small' />}
                  />
                ) : null}
              </Box>
            }
            secondary={`${t("serialnumber")}: ${device.sn}\n${t("relatedproject")}: ${device.project_alias ?? "(No Project)"}`}
          />
          {isPhone && isAlertDevice ? (
            <Box display='flex' alignItems='center' mt={1}>
              <Chip
                sx={{
                  p: 0.25,
                  backgroundColor: "#ff996688",
                  color: "black",
                  zIndex: 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: 10,
                }}
                label={alertText}
                icon={<Warning color='white' />}
              />
              <Tooltip title={alertText} sx={{ ml: 1 }}>
                <IconButton size='small' color='inherit'>
                  <InfoOutlined />
                </IconButton>
              </Tooltip>
            </Box>
          ) : null}
        </Box>
        {isPhone ? renderPhoneButtons() : renderDesktopButtons()}
      </ProtoListItem>
      <DeviceDescription
        device={device}
        expandIdx={expandIdx}
        deviceDesc={deviceDesc}
        sensorLatest={sensorLatest}
        historyButtonOnClick={() => handleHistoryButtonOnClick(device, deviceTypes)}
      />
    </li>
  );
};
